// frontend/src/features/shared/chat/hooks/useStickyAutoscroll.ts
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * #672/#673 — autoscroll policy for a chat log.
 *
 * Replaces the logic that used to live inline in MessageList, which had two
 * defects. First, it bound its scroll listener to MessageList’s own root; on
 * pages/chat.tsx that element never scrolls (an ancestor owns the overflow),
 * so the listener never fired and the opt-out flag sat at `false` forever —
 * autoscroll was unconditional. Second, on the surfaces where the listener
 * DID fire, the component’s own smooth scrollIntoView re-entered the
 * near-bottom zone and cleared the very flag it should have respected.
 *
 * The fixes here: the caller names the real scroll container, and the latch
 * arms only on scroll events preceded by genuine user input. A programmatic
 * scroll produces a scroll event with no wheel/touch/key before it, so it is
 * ignored.
 */

/** Keys that scroll a focused scroll container. */
const SCROLL_KEYS = new Set([
  'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ',
]);

/**
 * How long after a user input event a scroll still counts as user-driven.
 * Wheel momentum on macOS keeps emitting scroll events well after the last
 * wheel event, so this is generous; each new input refreshes it.
 */
const USER_INTENT_WINDOW_MS = 400;

/**
 * Read-only ref shape. Deliberately NOT React.RefObject<HTMLElement | null>:
 * RefObject’s `current` is mutable and therefore invariant, so a
 * RefObject<HTMLDivElement | null> — what useRef<HTMLDivElement>(null) returns
 * — would not be assignable. `readonly` makes the property covariant, so any
 * element ref is accepted. We only ever read `.current` here.
 */
export type ScrollElementRef = { readonly current: HTMLElement | null };

export interface UseStickyAutoscrollOptions {
  /** The element that actually owns the scroll — not necessarily the log. */
  containerRef: ScrollElementRef;
  /** Sentinel rendered after the last message. */
  endRef: ScrollElementRef;
  /** Changing values re-run the follow. Pass the messages + typing flag. */
  deps: React.DependencyList;
  /** Changing this clears the latch. Pass the conversation id. */
  resetKey?: string;
  /**
   * Changing this clears the latch AND re-anchors, whatever the value. Bump
   * it on user send: sending always re-anchors (spec B3). Distinct from
   * `resetKey` only in intent — a send does not change the conversation.
   */
  resetSignal?: number;
  /** Distance from the bottom still treated as "at the bottom", in px. */
  threshold?: number;
}

export interface StickyAutoscroll {
  /** True while the user has opted out of following new content. */
  isLatched: boolean;
  /** Re-arm following and jump to the newest content. */
  jumpToLatest: () => void;
}

export function useStickyAutoscroll({
  containerRef,
  endRef,
  deps,
  resetKey,
  resetSignal,
  threshold = 150,
}: UseStickyAutoscrollOptions): StickyAutoscroll {
  const [isLatched, setIsLatched] = useState(false);
  // Mirrored in a ref so the follow effect reads the current value without
  // taking `isLatched` as a dependency (which would re-run it on every latch
  // change and scroll the user back down).
  const latchedRef = useRef(false);
  const userIntentUntilRef = useRef(0);
  /** True between pointerdown and pointerup — a scrollbar drag in progress. */
  const pointerHeldRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  const setLatched = useCallback((next: boolean) => {
    if (latchedRef.current === next) return;
    latchedRef.current = next;
    setIsLatched(next);
  }, []);

  const scrollToEnd = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    // Coalescing: useStevensV4 calls setMessages on EVERY streamed token, so
    // without this the follow runs hundreds of times per turn. Cancelling the
    // pending frame collapses a burst into one scroll.
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      // 'auto', not 'smooth': a smooth animation re-issued per token overlaps
      // itself with undefined interleaving across engines, and the Capacitor
      // runtime is iOS WKWebView.
      endRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    });
  }, [endRef]);

  // Track user input so scroll events can be attributed.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const markIntent = () => {
      userIntentUntilRef.current = Date.now() + USER_INTENT_WINDOW_MS;
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (SCROLL_KEYS.has(e.key)) markIntent();
    };
    // Dragging the scrollbar thumb, clicking the track and middle-click
    // autoscroll all emit `scroll` with NO wheel/touch/key before them, so
    // without this a mouse user is still yanked mid-stream — #673's core
    // complaint. The pointer-held flag covers a drag that outlasts the
    // intent window, which a plain timestamp would not.
    const onPointerDown = () => {
      pointerHeldRef.current = true;
      markIntent();
    };
    const onPointerUp = () => {
      pointerHeldRef.current = false;
      markIntent();
    };
    const onScroll = () => {
      // Not our own scroll? A scroll with no user input behind it is one we
      // issued ourselves, and must never clear the user’s opt-out.
      if (!pointerHeldRef.current && Date.now() > userIntentUntilRef.current) return;
      const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
      setLatched(distance > threshold);
    };

    container.addEventListener('wheel', markIntent, { passive: true });
    container.addEventListener('touchmove', markIntent, { passive: true });
    container.addEventListener('keydown', onKeyDown);
    container.addEventListener('pointerdown', onPointerDown, { passive: true });
    // Fallback for engines that fire no pointer events (and for jsdom).
    container.addEventListener('mousedown', onPointerDown, { passive: true });
    container.addEventListener('scroll', onScroll, { passive: true });
    // Release can land outside the container — a scrollbar drag routinely
    // ends with the cursor off the element — so listen on the window.
    window.addEventListener('pointerup', onPointerUp, { passive: true });
    window.addEventListener('pointercancel', onPointerUp, { passive: true });
    window.addEventListener('mouseup', onPointerUp, { passive: true });
    return () => {
      container.removeEventListener('wheel', markIntent);
      container.removeEventListener('touchmove', markIntent);
      container.removeEventListener('keydown', onKeyDown);
      container.removeEventListener('pointerdown', onPointerDown);
      container.removeEventListener('mousedown', onPointerDown);
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      window.removeEventListener('mouseup', onPointerUp);
      pointerHeldRef.current = false;
    };
  }, [containerRef, setLatched, threshold]);

  // Follow new content unless latched.
  useEffect(() => {
    if (latchedRef.current) return;
    scrollToEnd();
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // A new conversation always starts followed. MessageList is not keyed by
  // conversation id, so without this a latch set in one thread survives into
  // the next one. `resetSignal` is the same reset driven by a user send
  // (spec B3): scrolling up and then sending must re-anchor, or the user
  // never sees their own message. Re-anchoring here rather than leaning on
  // the follow effect matters because a failed send produces no further
  // dependency change to piggyback on.
  useEffect(() => {
    setLatched(false);
    scrollToEnd();
  }, [resetKey, resetSignal, setLatched, scrollToEnd]);

  const jumpToLatest = useCallback(() => {
    userIntentUntilRef.current = 0;
    setLatched(false);
    scrollToEnd();
  }, [setLatched, scrollToEnd]);

  return { isLatched, jumpToLatest };
}
